import type { SupabaseClient } from '@supabase/supabase-js';
import { isVideoAccessible } from '$lib/utils/checkVideoAccess';
import type { User } from '$lib/types/user';

export async function getVideoSectors(balancedSupabase: SupabaseClient) {
	const { data: sectors, error } = await balancedSupabase
		.from('video_sectors')
		.select(
			`
				*
			`
		)
		// .limit(12, { foreignTable: 'videos' })
		.limit(10)
		.order('order', { ascending: true });
	if (error) {
		console.error(error);
		return [];
	}
	return sectors;
}

export async function getVideo(balancedSupabase: SupabaseClient, videoId: string) {
	const { data: video, error } = await balancedSupabase
		.from('videos')
		.select('*')
		.eq('id', videoId)
		.single();
	if (error) {
		console.error(error);
		return null;
	}
	return video;
}

export async function getVideos(
	balancedSupabase: SupabaseClient,
	user: User | null,
	url: URL,
	params,
	start: number,
	end: number
) {
	console.log('getVideos called');
	const pageSize = end - start + 1;
	const pageNumber = Math.floor(start / pageSize) + 1;

	console.log('params', params);
	// Initialize the base parameters
	let dbParams: {
		page: number;
		page_size: number;
		search_terms?: string[];
		sort_column?: string;
		sort_direction?: string;
		filter_sector_ids?: string[];
		filter_category_ids?: string[];
		filter_tool_ids?: string[];
		filter_tag_ids?: string[];
		filter_material_ids?: string[];
		filter_language?: string;
		filter_free?: boolean;
		filter_public: boolean;
		filter_level?: string;
	} = {
		page: pageNumber,
		page_size: pageSize,
		filter_public: params.filter_public !== false
	};

	// Helper function to add parameter if it exists in URL
	const addParamIfExists = (urlParam: string, funcParam: string) => {
		const value = url.searchParams.get(urlParam);
		if (value !== null) {
			dbParams[funcParam as keyof typeof dbParams] = value.split(',');
		}
	};

	// Add search_terms if query is set
	if (url.searchParams.has('search')) {
		const query = url.searchParams.get('search') as string;
		if (query !== '') {
			dbParams.search_terms = query.trim().split(' ');
		}
	}

	// Add other parameters if they exist in the URL
	addParamIfExists('category', 'filter_category_ids');
	addParamIfExists('material', 'filter_material_ids');
	addParamIfExists('tool', 'filter_tool_ids');
	addParamIfExists('language', 'filter_language');
	addParamIfExists('level', 'filter_level');

	// Handle free parameter
	if (url.searchParams.has('free') || url.pathname.split('/').includes('free')) {
		dbParams.filter_free = true;
	}

	if (
		url.searchParams.has('latest') ||
		url.pathname.split('/').includes('latest') ||
		!url.searchParams.has('search')
	) {
		dbParams.sort_column = 'created_at';
		dbParams.sort_direction = 'desc';
	}

	// Handle promotional
	if (url.searchParams.has('promotional') || url.pathname.split('/').includes('promotional')) {
		// Handle promotional logic if needed
		dbParams.filter_category_ids = [
			'dcaeba5a-a8ea-4d69-a6f8-ace0ee1d6fd5',
			'12007d7a-425c-47ab-9860-3379a5752036'
		]; // Promotional & Product Videos
	}

	// Add sorting parameters
	if (url.searchParams.get('sortColumn')) {
		dbParams.sort_column = url.searchParams.get('sortColumn') as string;
	}
	if (url.searchParams.get('sortDirection')) {
		dbParams.sort_direction = url.searchParams.get('sortDirection') as string;
	}

	// Handle sector (assuming it's passed as sectorSlug in params)
	if (params.sectorSlug) {
		const { data: sector, error } = await balancedSupabase
			.from('video_sectors')
			.select('id')
			.eq('slug', params.sectorSlug)
			.single();
		if (error) {
			console.error(error);
			return {};
		}

		dbParams.filter_sector_ids = [sector.id];
	} else {
		addParamIfExists('sector', 'filter_sector_ids');
	}

	// Handle tags (level, 'car brand', color, part)
	const tagParams = ['level', 'car brand', 'color', 'part'];
	let tags: string[] = [];
	tagParams.forEach((param) => {
		const value = url.searchParams.get(param);
		if (value) {
			tags = [...tags, ...value.split(',')];
		}
	});
	if (tags.length > 0) {
		dbParams.filter_tag_ids = tags;
	}

	// Call the RPC function with the params object
	const { data, error } = await balancedSupabase.rpc('get_videos', dbParams);

	if (error) {
		console.error(error);
		return { data: [], count: 0 };
	}

	let count = 0;
	if (data && data.length > 0) {
		count = data[0].total_count;
	}

	const updatedData = data.map((video) => ({
		...video,
		locked: !isVideoAccessible(video, user)
	}));

	return { data: updatedData, count };
}

export async function getPicks(balancedSupabase: SupabaseClient) {
	const { data: picks, error } = await balancedSupabase
		.from('videos')
		.select(
			`
                *,
                sector:video_sectors(*)
            `
		)
		.order('name', { ascending: true })
		.eq('pick', true);
	if (error) {
		console.error(error);
		return [];
	}
	return picks;
}
